import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  login as loginRequest,
  logout as logoutRequest,
  obtenerUsuarioActual,
  type AuthUser,
} from '../lib/auth';
import { getRefreshToken } from '../lib/tokenStorage';
import { subscribirAuthLost } from '../lib/api';

interface AuthContextValue {
  usuario: AuthUser | null;
  cargando: boolean;
  iniciarSesion: (email: string, password: string) => Promise<void>;
  cerrarSesion: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<AuthUser | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    void (async () => {
      const refresh = await getRefreshToken();
      if (!refresh) {
        setCargando(false);
        return;
      }
      try {
        const u = await obtenerUsuarioActual();
        setUsuario(u);
      } catch (e) {
        // Falló por algo distinto a 401 (ej. 429 rate limit, 5xx, red).
        // Mantener sesión "tentativa": no limpiar tokens y dejar que el siguiente
        // request o el sync worker reintenten cuando el server esté disponible.
        // Si era un 401 real, obtenerUsuarioActual habría devuelto null.
        console.warn('No se pudo restaurar sesión, manteniendo tokens locales:', e);
      } finally {
        setCargando(false);
      }
    })();

    return subscribirAuthLost(() => {
      setUsuario(null);
    });
  }, []);

  const iniciarSesion = useCallback(async (email: string, password: string) => {
    const u = await loginRequest(email, password);
    setUsuario(u);
  }, []);

  const cerrarSesion = useCallback(async () => {
    await logoutRequest();
    setUsuario(null);
  }, []);

  return (
    <AuthContext.Provider value={{ usuario, cargando, iniciarSesion, cerrarSesion }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}
